import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { Barcode, Upload, CheckCircle2, AlertTriangle, Search, FileSpreadsheet, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { inv, INV_API } from '../../lib/invApi';
import ScanButton from '../../components/inventory/ScanButton';

const ACTION_STYLE = {
  create_item: 'text-green-400',
  link_existing_item: 'text-blue-400',
  already_registered: 'text-[#A3A3A3]',
  duplicate_in_file: 'text-amber-400',
  error: 'text-red-400',
};

const InventoryBarcodes = () => {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [q, setQ] = useState('');
  const [loading, setLoading] = useState(false);

  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [reviewed, setReviewed] = useState(false);
  const [importing, setImporting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await inv.get('/barcodes', { q: q || undefined });
      setRows(data.barcodes || []);
      setTotal(data.total_registered || 0);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to load'); }
    setLoading(false);
  }, [q]);

  useEffect(() => { load(); }, [load]);

  const runImport = async (commit) => {
    if (!file) { toast.error('Choose a CSV or XLSX file first'); return; }
    setImporting(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const { data } = await axios.post(`${INV_API}/barcodes/import?commit=${commit}`, fd, { withCredentials: true });
      setPreview(data);
      if (commit) {
        toast.success('Import committed');
        setReviewed(false); setFile(null); setPreview(null);
        await load();
      } else {
        setReviewed(false);
        toast.success('Preview ready — review below');
      }
    } catch (e) { toast.error(e.response?.data?.detail || 'Import failed'); }
    setImporting(false);
  };

  const s = preview?.summary;

  return (
    <div data-testid="inv-barcodes-page" className="space-y-6">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h2 className="font-['Outfit'] text-3xl font-bold text-white mb-1 flex items-center gap-3">
            <Barcode className="w-7 h-7 text-[#D4AF37]" /> Barcode Registry
          </h2>
          <p className="text-[#A3A3A3] text-sm">One unique barcode links to one Master Inventory item. Label copies are print-only and never create duplicate items.</p>
        </div>
        <ScanButton mode="scan" testId="barcodes-scan-btn" />
      </div>

      {/* Import */}
      <section className="bg-[#141414] border border-[#262626] rounded-md p-5 space-y-4">
        <h3 className="text-white font-semibold flex items-center gap-2"><Upload className="w-4 h-4 text-[#D4AF37]" /> Import Barcode Mapping (CSV / XLSX)</h3>
        <p className="text-[#A3A3A3] text-xs">Columns: Barcode, Part Name, Source Group, Label Copies, Notes. Re-importing the same file is idempotent — no duplicates.</p>
        <div className="flex flex-wrap items-center gap-3">
          <input data-testid="barcode-import-file" type="file" accept=".csv,.xlsx,.xls"
            onChange={(e) => { setFile(e.target.files?.[0] || null); setPreview(null); setReviewed(false); }}
            className="text-sm text-[#A3A3A3] file:mr-3 file:py-2 file:px-4 file:rounded-md file:border-0 file:bg-[#262626] file:text-white" />
          <Button data-testid="barcode-import-preview" onClick={() => runImport(false)} disabled={!file || importing} variant="outline" className="border-[#262626] text-white hover:bg-[#262626]">
            <FileSpreadsheet className="w-4 h-4 mr-2" /> Preview
          </Button>
        </div>

        {preview && s && (
          <div data-testid="barcode-import-preview-result" className="space-y-3">
            <div className="grid grid-cols-2 md:grid-cols-6 gap-2 text-center">
              {[
                ['Rows', s.total_rows, 'text-white'],
                ['New items', s.create_item, 'text-green-400'],
                ['Linked', s.link_existing_item, 'text-blue-400'],
                ['Already registered', s.already_registered, 'text-[#A3A3A3]'],
                ['Duplicates in file', s.duplicate_in_file, 'text-amber-400'],
                ['Errors', s.errors, s.errors > 0 ? 'text-red-400' : 'text-[#525252]'],
              ].map(([lbl, val, cls]) => (
                <div key={lbl} className="bg-[#0A0A0A] border border-[#262626] rounded-md p-2">
                  <p className={`text-xl font-bold ${cls}`}>{val}</p>
                  <p className="text-[10px] uppercase tracking-wider text-[#525252]">{lbl}</p>
                </div>
              ))}
            </div>

            <div className="max-h-64 overflow-y-auto border border-[#262626] rounded-md">
              <table className="w-full text-xs">
                <thead className="bg-[#0A0A0A] sticky top-0"><tr>
                  <th className="text-left px-2 py-1 text-[#A3A3A3]">#</th>
                  <th className="text-left px-2 py-1 text-[#A3A3A3]">Barcode</th>
                  <th className="text-left px-2 py-1 text-[#A3A3A3]">Part / Item</th>
                  <th className="text-left px-2 py-1 text-[#A3A3A3]">Action</th>
                  <th className="text-left px-2 py-1 text-[#A3A3A3]">Message</th>
                </tr></thead>
                <tbody>
                  {(preview.rows || []).slice(0, 300).map((r) => (
                    <tr key={r.row} className="border-t border-[#262626]">
                      <td className="px-2 py-1 text-[#525252]">{r.row}</td>
                      <td className="px-2 py-1 font-mono text-white">{r.barcode}</td>
                      <td className="px-2 py-1 text-white">{r.item_name || r.part_name}</td>
                      <td className={`px-2 py-1 font-medium ${ACTION_STYLE[r.action] || 'text-white'}`}>{r.action}</td>
                      <td className="px-2 py-1 text-[#A3A3A3]">{r.message}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {preview.mode === 'preview' && (
              <div className="flex flex-wrap items-center gap-3">
                <label className="flex items-center gap-2 text-sm text-white cursor-pointer">
                  <input data-testid="barcode-import-reviewed" type="checkbox" checked={reviewed} onChange={(e) => setReviewed(e.target.checked)} />
                  I reviewed the rows above
                </label>
                <Button data-testid="barcode-import-commit" onClick={() => runImport(true)} disabled={!reviewed || importing}
                  className="bg-[#D4AF37] text-[#0A0A0A] hover:bg-[#F3E5AB]">
                  <CheckCircle2 className="w-4 h-4 mr-2" /> Confirm & Import
                </Button>
              </div>
            )}
          </div>
        )}
      </section>

      {/* Registry */}
      <section className="space-y-3">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <h3 className="text-white font-semibold">Registered Barcodes <span className="text-[#525252] text-sm">({total})</span></h3>
          <div className="flex items-center gap-2">
            <div className="relative">
              <Search className="w-4 h-4 text-[#525252] absolute left-2 top-2.5" />
              <Input data-testid="barcode-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search barcode / part…" className="pl-8 bg-[#0A0A0A] border-[#262626] text-white w-64" />
            </div>
            <Button data-testid="barcode-refresh" onClick={load} variant="outline" className="border-[#262626] text-white hover:bg-[#262626]"><RefreshCw className="w-4 h-4" /></Button>
          </div>
        </div>

        <div className="bg-[#141414] border border-[#262626] rounded-md overflow-x-auto">
          <table className="w-full min-w-[720px] text-sm">
            <thead className="bg-[#0A0A0A] border-b border-[#262626]"><tr>
              <th className="text-left px-3 py-2 text-[#A3A3A3]">Barcode</th>
              <th className="text-left px-3 py-2 text-[#A3A3A3]">Master Inventory Item</th>
              <th className="text-left px-3 py-2 text-[#A3A3A3]">Source Group</th>
              <th className="text-right px-3 py-2 text-[#A3A3A3]">Label Copies</th>
              <th className="text-left px-3 py-2 text-[#A3A3A3]">Status</th>
            </tr></thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={5} className="px-3 py-6 text-center text-[#525252]">Loading…</td></tr>
              ) : rows.length === 0 ? (
                <tr><td colSpan={5} className="px-3 py-6 text-center text-[#525252]">No barcodes registered yet. Import a mapping file above.</td></tr>
              ) : rows.map((r) => (
                <tr key={r.id} data-testid={`barcode-row-${r.barcode}`} className="border-b border-[#262626] last:border-0">
                  <td className="px-3 py-2 font-mono text-white">{r.barcode}</td>
                  <td className="px-3 py-2 text-white">{r.item_name || r.part_name || '—'}{r.item_archived && <span className="ml-2 text-[10px] text-amber-400">(archived)</span>}</td>
                  <td className="px-3 py-2 text-[#A3A3A3]">{r.source_group || '—'}</td>
                  <td className="px-3 py-2 text-right text-[#A3A3A3]">{r.label_copies}</td>
                  <td className="px-3 py-2">{r.active ? <span className="text-green-400 text-xs">Active</span> : <span className="text-[#525252] text-xs">Inactive</span>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
};

export default InventoryBarcodes;
